import { cn } from "@/lib/utils";
import type { RiskLevel } from "@/domain/risk";

/**
 * THE SIGHT GLASS.
 *
 * The one ornament carried whole from the marketing site into the app: a tube of liquid
 * with surface tension at its meniscus. It earns its place because it is the fastest way
 * a human reads a tank — faster than a number, faster than a bar. An ops manager scanning
 * twelve sites at 7am sees the short columns before they read a single word.
 *
 * Colour is not decoration here: it is the gauge saying one thing. Red is "this costs you
 * money today".
 */

const FILL: Record<RiskLevel, string> = {
  dry: "text-dry",
  low: "text-low",
  overdue: "text-mauve",
  ok: "text-ok",
};

export interface SightGlassProps {
  /** 0–100. Null when the tank has never been read — the glass shows empty and says so. */
  percent: number | null;
  level?: RiskLevel;
  className?: string;
  /** Accessible description; the glass is otherwise a pure graphic. */
  label?: string;
}

export function SightGlass({ percent, level = "ok", className, label }: SightGlassProps) {
  const filled = percent ?? 0;
  const known = percent !== null;

  return (
    <div
      className={cn("glass h-16 w-6 shrink-0", className)}
      role="img"
      aria-label={label ?? (known ? `${Math.round(filled)}%` : "level unknown")}
    >
      {known ? (
        <div
          className={cn("glass-fill", FILL[level])}
          style={{ height: `${Math.min(Math.max(filled, 0), 100)}%`, background: "currentColor" }}
        />
      ) : (
        // An unknown level is not an empty tank. Hatching says "no reading", not "no fuel".
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, transparent, transparent 3px, var(--color-rule-strong) 3px, var(--color-rule-strong) 4px)",
          }}
        />
      )}
    </div>
  );
}

/** The horizontal reading of the same instrument, for dense table rows. */
export function LevelBar({
  percent,
  level = "ok",
  className,
}: {
  percent: number | null;
  level?: RiskLevel;
  className?: string;
}) {
  const known = percent !== null;
  return (
    <div
      className={cn(
        "h-1.5 w-full min-w-16 overflow-hidden rounded-full bg-paper-sunk",
        className,
      )}
      role="img"
      aria-label={known ? `${Math.round(percent)}%` : "level unknown"}
    >
      {known ? (
        <div
          className={cn("h-full rounded-full transition-[width] duration-500", FILL[level])}
          style={{
            width: `${Math.min(Math.max(percent, 0), 100)}%`,
            background: "currentColor",
          }}
        />
      ) : null}
    </div>
  );
}
